import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Truck, RefreshCw } from "lucide-react";
import api, { money } from "@/lib/api";

const inputCls = "w-full bg-black border border-zinc-800 focus:border-[#C87455] text-white text-sm px-3 py-2.5 rounded-none outline-none placeholder-zinc-600";
const PROVINCES = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];

export const ShippingStep = ({ items, onBack, onContinue, loading, onNavigate }) => {
  const [addr, setAddr] = useState(() => { try { return JSON.parse(localStorage.getItem("es_ship_addr") || "null") || { name: "", line1: "", city: "", state: "AB", postal_code: "", country: "CA" }; } catch { return { name: "", line1: "", city: "", state: "AB", postal_code: "", country: "CA" }; } });
  const [rates, setRates] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState("");
  const set = (k) => (e) => {
    const v = e.target.value;
    setAddr((a) => (k === "country" ? { ...a, country: v, state: v === "CA" ? "AB" : "", postal_code: "" } : { ...a, [k]: v }));
    setRates(null); setSelected("");
  };
  const itemsKey = JSON.stringify(items.map((i) => [i.lookup_key, i.quantity]));

  useEffect(() => { setRates(null); setSelected(""); }, [itemsKey]);
  useEffect(() => { localStorage.setItem("es_ship_addr", JSON.stringify(addr)); }, [addr]);

  const postalOk = addr.country === "CA" ? /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/.test(addr.postal_code.trim()) : /^\d{5}(-\d{4})?$/.test(addr.postal_code.trim());
  const addressOk = addr.name.trim() && addr.line1.trim() && addr.city.trim() && addr.state.trim() && postalOk;

  const getRates = async () => {
    if (!addressOk) { setError(addr.country === "CA" ? "Please complete the address with a valid postal code (e.g. T9E 6Z9)." : "Please complete the address with a valid ZIP code."); return; }
    setError(""); setFetching(true); setRates(null); setSelected("");
    try {
      const { data } = await api.post("/shipping/rates", { items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.quantity })), address: addr });
      setRates(data);
      if (data.services?.length === 1) setSelected(data.services[0].code);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not calculate shipping. Please try again.");
    }
    setFetching(false);
  };

  const proceed = () => {
    if (!rates || !selected) return;
    if (rates.fallback) return onContinue({ method: "flat", quote_id: rates.id, address: addr });
    onContinue({ method: "quote", quote_id: rates.id, service_code: selected, address: addr });
  };

  const chosen = rates?.services?.find((s) => s.code === selected);

  return (
    <div className="space-y-5" data-testid="shipping-step">
      <div className="flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#C87455]" data-testid="ship-mode-ship">
        <Truck size={12} /> Ship to me · Canada Post · calculated from your address
      </div>

      {(
        <div className="space-y-3" data-testid="ship-address-form">
          <div className="grid grid-cols-2 gap-3">
            <select value={addr.country} onChange={set("country")} className={inputCls} data-testid="ship-country"><option value="CA">Canada</option><option value="US">United States</option></select>
            <input value={addr.name} onChange={set("name")} placeholder="Full name" className={inputCls} data-testid="ship-name" />
          </div>
          <input value={addr.line1} onChange={set("line1")} placeholder="Street address" className={inputCls} data-testid="ship-line1" />
          <div className="grid grid-cols-3 gap-3">
            <input value={addr.city} onChange={set("city")} placeholder="City" className={`${inputCls} col-span-1`} data-testid="ship-city" />
            {addr.country === "CA" ? (
              <select value={addr.state} onChange={set("state")} className={inputCls} data-testid="ship-state">{PROVINCES.map((p) => <option key={p}>{p}</option>)}</select>
            ) : (
              <input value={addr.state} onChange={set("state")} placeholder="State" className={inputCls} data-testid="ship-state" />
            )}
            <input value={addr.postal_code} onChange={set("postal_code")} placeholder={addr.country === "CA" ? "Postal code" : "ZIP"} className={inputCls} data-testid="ship-postal" />
          </div>
          <button type="button" onClick={getRates} disabled={fetching} data-testid="get-rates-btn"
            className="w-full border border-zinc-700 hover:border-[#C87455] text-zinc-200 py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-60">
            {fetching ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} {rates ? "Refresh rates" : "Calculate shipping"}
          </button>
          {error && <p className="text-amber-300 text-xs" data-testid="ship-error">{error}</p>}

          {rates && (
            <div className="space-y-2" data-testid="ship-rates">
              {rates.fallback && (
                <div className="border border-amber-400/30 bg-amber-400/5 p-3" data-testid="ship-fallback-notice">
                  <p className="text-amber-200 text-xs leading-relaxed">{rates.reason} You can continue with flat-rate shipping, or <Link to="/quote?type=shipping" onClick={onNavigate} className="underline text-[#C87455]" data-testid="manual-shipping-quote-link">request a manual shipping quote</Link>.</p>
                </div>
              )}
              {rates.services.map((s) => (
                <button key={s.code} type="button" onClick={() => setSelected(s.code)} data-testid={`ship-service-${s.code}`} aria-pressed={selected === s.code}
                  className={`w-full text-left border p-3 flex items-center justify-between gap-3 ${selected === s.code ? "border-[#C87455] bg-[#C87455]/10" : "border-zinc-800 hover:border-zinc-600"}`}>
                  <div>
                    <p className="text-sm text-zinc-100">{s.name}</p>
                    <p className="text-[11px] text-zinc-500">{s.detail}{s.parcels > 1 ? ` · ${s.parcels} parcels` : ""}</p>
                  </div>
                  <span className="font-mono-tech text-sm text-zinc-200 whitespace-nowrap">{s.amount ? money(s.amount) : "Free"}</span>
                </button>
              ))}
              {!rates.fallback && <p className="font-mono-tech text-[9px] uppercase tracking-[0.2em] text-zinc-600">Live Canada Post rates · production time 5–7 business days is separate from transit · GST added at payment</p>}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onBack} data-testid="ship-back-btn" className="border border-zinc-700 text-zinc-300 px-5 py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em]">Back</button>
        <button type="button" onClick={proceed} disabled={loading || !chosen} data-testid="ship-continue-btn"
          className="flex-1 bg-[#C87455] text-black hover:bg-[#A65B3E] transition-colors px-6 py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
          {loading ? <Loader2 size={12} className="animate-spin" /> : `Continue to payment${chosen ? ` · shipping ${chosen.amount ? money(chosen.amount) : "free"}` : ""}`}
        </button>
      </div>
    </div>
  );
};
